import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Cookie, Settings2, X, Info } from "lucide-react";
import { Link } from "react-router-dom";
import { useLanguage } from "@/i18n/LanguageContext";

const CONSENT_KEY = "kaufyard-cookie-consent";

export interface ConsentData {
  accepted: boolean;
  essential: boolean;
  functional: boolean;
  analytics: boolean;
  marketing: boolean;
  timestamp: string;
  version: number;
}

export function getCookieConsent(): ConsentData | null {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function hasAnalyticsConsent(): boolean {
  const consent = getCookieConsent();
  return consent?.analytics === true;
}

export function hasFunctionalConsent(): boolean {
  const consent = getCookieConsent();
  return consent?.functional === true;
}

const CookieConsent = () => {
  const { t } = useLanguage();
  // Initialize synchronously to avoid post-mount layout shift (CLS)
  const [visible, setVisible] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    try {
      return !localStorage.getItem(CONSENT_KEY);
    } catch {
      return false;
    }
  });
  const [showPanel, setShowPanel] = useState(false);
  const [functional, setFunctional] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  const saveConsent = (prefs: { functional: boolean; analytics: boolean; marketing: boolean }) => {
    const data: ConsentData = {
      accepted: true,
      essential: true,
      functional: prefs.functional,
      analytics: prefs.analytics,
      marketing: prefs.marketing,
      timestamp: new Date().toISOString(),
      version: 1,
    };
    localStorage.setItem(CONSENT_KEY, JSON.stringify(data));
    setVisible(false);
    setShowPanel(false);
  };

  const handleAcceptAll = () => {
    saveConsent({ functional: true, analytics: true, marketing: true });
  };

  const handleNecessaryOnly = () => {
    saveConsent({ functional: false, analytics: false, marketing: false });
  };

  const handleSaveCustom = () => {
    saveConsent({ functional, analytics, marketing });
  };

  if (!visible) return null;

  const cc = t.cookieConsent || {} as any;

  return (
    <div className="fixed inset-0 z-[9999] flex items-end justify-center bg-black/40 p-4 md:p-6">
      <Card className="mx-auto w-full max-w-2xl border-border bg-card p-5 shadow-2xl">
        {!showPanel ? (
          /* Main banner */
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <Cookie className="h-6 w-6 text-accent shrink-0 mt-0.5" />
              <div className="flex-1">
                <h2 className="text-lg font-semibold text-foreground mb-2">
                  {cc.title || "Cookie-Einstellungen"}
                </h2>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {cc.text || "Wir verwenden Cookies, um Ihnen die bestmögliche Erfahrung zu bieten. Essenzielle Cookies sind für den Betrieb der Website erforderlich. Analyse-Cookies helfen uns, die Website zu verbessern."}
                </p>
                <p className="text-xs text-muted-foreground mt-2">
                  {cc.moreInfo || "Weitere Informationen finden Sie in unserer"}{" "}
                  <Link to="/cookies" className="underline text-primary hover:text-primary/80">
                    {cc.cookiePolicy || "Cookie-Erklärung"}
                  </Link>{" "}
                  {cc.and || "und"}{" "}
                  <Link to="/datenschutz" className="underline text-primary hover:text-primary/80">
                    {cc.privacyPolicy || "Datenschutzerklärung"}
                  </Link>.
                </p>
              </div>
            </div>

            {/* GDPR-compliant: buttons have EQUAL size and prominence */}
            <div className="flex flex-col sm:flex-row gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={handleAcceptAll}
                className="flex-1 font-medium"
              >
                {cc.acceptAll || "Alle akzeptieren"}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={handleNecessaryOnly}
                className="flex-1 font-medium"
              >
                {cc.necessaryOnly || "Nur notwendige"}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowPanel(true)}
                className="flex-1 font-medium gap-1"
              >
                <Settings2 className="h-3.5 w-3.5" />
                {cc.settings || "Einstellungen"}
              </Button>
            </div>
          </div>
        ) : (
          /* Detailed settings panel */
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
                <Settings2 className="h-5 w-5" />
                {cc.detailedSettings || "Cookie-Einstellungen"}
              </h2>
              <Button variant="ghost" size="sm" onClick={() => setShowPanel(false)}>
                <X className="h-4 w-4" />
              </Button>
            </div>

            <div className="space-y-3">
              {/* Essential - locked ON */}
              <div className="flex items-center justify-between rounded-lg bg-muted/50 p-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-foreground">{cc.essential || "Essenzielle Cookies"}</p>
                    <span className="text-[10px] font-medium text-muted-foreground bg-muted px-1.5 py-0.5 rounded">{cc.required || "Erforderlich"}</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">{cc.essentialDesc || "Authentifizierung, Sicherheit, Grundfunktionen"}</p>
                </div>
                <Switch checked disabled className="opacity-60" />
              </div>

              {/* Functional */}
              <div className="flex items-center justify-between rounded-lg bg-muted/50 p-3">
                <div className="flex-1">
                  <p className="text-sm font-medium text-foreground">{cc.functional || "Funktionale Cookies"}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{cc.functionalDesc || "Zuletzt angesehene Artikel, UI-Präferenzen"}</p>
                </div>
                <Switch checked={functional} onCheckedChange={setFunctional} />
              </div>

              {/* Analytics */}
              <div className="flex items-center justify-between rounded-lg bg-muted/50 p-3">
                <div className="flex-1">
                  <p className="text-sm font-medium text-foreground">{cc.analytics || "Analyse-Cookies"}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{cc.analyticsDesc || "Nutzungsanalyse zur Verbesserung der Website"}</p>
                </div>
                <Switch checked={analytics} onCheckedChange={setAnalytics} />
              </div>

              {/* Marketing */}
              <div className="flex items-center justify-between rounded-lg bg-muted/50 p-3">
                <div className="flex-1">
                  <p className="text-sm font-medium text-foreground">{cc.marketing || "Marketing-Cookies"}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{cc.marketingDesc || "Derzeit nicht verwendet"}</p>
                </div>
                <Switch checked={marketing} onCheckedChange={setMarketing} />
              </div>
            </div>

            {/* Equal prominence buttons */}
            <div className="flex flex-col sm:flex-row gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={handleSaveCustom}
                className="flex-1 font-medium"
              >
                {cc.save || "Auswahl speichern"}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={handleAcceptAll}
                className="flex-1 font-medium"
              >
                {cc.acceptAll || "Alle akzeptieren"}
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};

export default CookieConsent;
